import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const IntegratedCare: React.FC = () => {
  const navigate = useNavigate()
  
  // Reading progress
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  
  // Active section highlight
  const [activeId, setActiveId] = useState<string>('exec-summary')

  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: "exec-summary", labelKey: "IntegratedCare.sections.executiveSummary" },
    { id: "cost-fragmentation", labelKey: "IntegratedCare.sections.costFragmentation" },
    { id: "integration-matters", labelKey: "IntegratedCare.sections.integrationMatters" },
    { id: "key-areas", labelKey: "IntegratedCare.sections.keyAreas" },
    { id: "benefits", labelKey: "IntegratedCare.sections.benefits" },
    { id: "roadmap", labelKey: "IntegratedCare.sections.roadmap" },
    { id: "conclusion", labelKey: "IntegratedCare.sections.conclusion" },
    { id: "references", labelKey: "IntegratedCare.sections.references" },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: "-40% 0px -50% 0px", threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Smooth scroll to anchors
  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // Get actual header height dynamically
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80 // 16px extra padding
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: "smooth" })
  }

  // Helper UI component
  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Reading progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | Integrated Care Coordination
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            Breaking Silos: How Integrated Care Coordination Improves Outcomes and Reduces Costs
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Fragmentation remains one of the greatest challenges in modern healthcare. Patients often navigate across multiple providers and services with little coordination, resulting in duplicated tests, higher costs, poorer outcomes, and frustration.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX Research Group" />
            <MetaItem label="Published" value="August 25, 2025" />
            <MetaItem label="Length" value="11 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: "Breaking Silos: How Integrated Care Coordination Improves Outcomes and Reduces Costs", url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      {/* Content & Sidebar */}
      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article */}
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? "mt-10" : ""}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === "exec-summary" && "⭐ Executive Summary"}
                {s.id === "cost-fragmentation" && "🤖 1. The Cost of Fragmentation"}
                {s.id === "integration-matters" && "📊 2. Why Integration Matters Now"}
                {s.id === "key-areas" && "💡 3. Key Areas for Coordination"}
                {s.id === "benefits" && "⚠️ 4. Benefits of Integrated Platforms"}
                {s.id === "roadmap" && "🛠️ 5. Roadmap to Breaking Silos"}
                {s.id === "conclusion" && "✅ 6. Conclusion"}
                {s.id === "references" && "📚 References"}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === "exec-summary" && (
                  <>
                    <p>
                      Fragmentation remains one of the greatest challenges in modern healthcare. Patients often navigate across multiple providers and services with little coordination, resulting in duplicated tests, higher costs, poorer outcomes, and frustration. The financial impact is staggering: between $760 billion and $935 billion in waste annually in the U.S. alone [1].
                    </p>
                    <br />
                    <p>
                      Integrated care coordination powered by platforms like EceptionistCX provides a scalable solution. By breaking down silos across referrals, scheduling, case management, and virtual consultations, health systems can improve outcomes, reduce costs, and deliver more equitable care [2][3].
                    </p>
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">Key Insight:</span> Siloed systems lead to inefficiency and waste.</p>
                    </div>
                  </>
                )}
                {s.id === "cost-fragmentation" && (
                  <>
                    <p>
                      Siloed systems lead to inefficiency and waste:
                    </p>
                    <br />
                    <ul>
                      <li>- Duplicated tests and miscommunication between providers [1].</li>
                      <li>- Increased risk of diagnostic errors [2].</li>
                      <li>- Higher readmission rates and unnecessary utilization [2].</li>
                      <li>- Staff frustration and burnout from manual rework.</li>
                    </ul>
                    <br />
                    <p>
                      The result is wasted resources, patient dissatisfaction, and financial strain on health systems.
                    </p>
                  </>
                )}
                {s.id === "integration-matters" && (
                  <>
                    <p>
                      The shift toward value-based care models and bundled payments is accelerating the need for integration. Payers and regulators expect coordinated systems that deliver both quality and efficiency. Health systems that cannot demonstrate integrated workflows risk financial penalties and reputational harm [2][3].
                    </p>
                  </>
                )}
                {s.id === "key-areas" && (
                  <>
                    <p>
                      Effective integration touches multiple workflows:
                    </p>
                    <br />
                    <ul>
                      <li>- Referral management across services.</li>
                      <li>- Scheduling optimization across sites.</li>
                      <li>- Case management for chronic or complex conditions.</li>
                      <li>- Virtual consults and telehealth integration.</li>
                      <li>- Vaccination and population health tracking.</li>
                    </ul>
                    <br />
                    <p>
                      An integrated platform like EceptionistCX supports these areas within a single framework.
                    </p>
                  </>
                )}
                {s.id === "benefits" && (
                  <>
                    <p>
                      Health systems adopting integrated platforms report measurable benefits:
                    </p>
                    <br />
                    <ul>
                      <li>- Lower readmission rates by up to 20% [2].</li>
                      <li>- Improved patient satisfaction and engagement [3].</li>
                      <li>- Reduced administrative costs from streamlined workflows [1].</li>
                      <li>- Enhanced reporting for compliance and quality metrics.</li>
                    </ul>
                    <br />
                    <p>
                      Integration improves both outcomes and financial sustainability.
                    </p>
                  </>
                )}
                {s.id === "roadmap" && (
                  <>
                    <p>
                      To achieve true integration, health systems should:
                    </p>
                    <br />
                    <ol>
                      <li>1. Assess workflows and identify silos.</li>
                      <li>2. Choose interoperable, SaaS-based platforms that integrate with EHRs.</li>
                      <li>3. Standardize workflows across departments.</li>
                      <li>4. Train staff and foster collaboration.</li>
                      <li>5. Monitor KPIs and continuously optimize.</li>
                    </ol>
                    <br />
                    <p>
                      This approach builds a sustainable, scalable model for integrated care.
                    </p>
                  </>
                )}
                {s.id === "conclusion" && (
                  <>
                    <p>
                      Healthcare cannot achieve the triple aim of better care, lower costs, and improved population health without integration. By breaking down silos with platforms like EceptionistCX, health systems can reduce waste, improve patient experience, and strengthen financial performance [1][2][3].
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}
                {s.id === "references" && (
                  <>
                    <ol>
                      <li>1. JAMA. Waste in the U.S. Healthcare System: Estimated Costs and Potential for Savings. 2019.</li>
                      <li>2. Agency for Healthcare Research and Quality (AHRQ). Diagnostic Errors and Miscommunication. 2020.</li>
                      <li>3. Health Affairs. Integrated Care Models and Readmission Reduction. 2020.</li>
                    </ol>
                    <div className="mt-6 rounded-xl border border-slate-200 p-4">
                      <h4 className="text-lg font-semibold mb-2">📈 Appendix: Charts and Visuals</h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Estimated Annual U.S. Healthcare Waste (USD B)</li>
                        <li>• Benefits of Integrated Care Coordination (%)</li>
                        <li>• Impact of Integrated Platforms on Communication</li>
                      </ul>
                    </div>

                    {/* Charts and Visuals Images */}
                    <div className="mt-6 space-y-6">
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Estimated Annual U.S. Healthcare Waste</h5>
                        <img 
                          src="/Image/WhitePapers/IntegratedCare_WhitePaper_New_Format/image1.png" 
                          alt="Estimated Annual U.S. Healthcare Waste Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Benefits of Integrated Care Coordination</h5>
                        <img 
                          src="/Image/WhitePapers/IntegratedCare_WhitePaper_New_Format/image2.png" 
                          alt="Benefits of Integrated Care Coordination Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Impact of Integrated Platforms on Communication</h5>
                        <img 
                          src="/Image/WhitePapers/IntegratedCare_WhitePaper_New_Format/image3.png" 
                          alt="Impact of Integrated Platforms on Communication Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          ))}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                This white paper explores how integrated care coordination breaks down silos to improve outcomes, reduce costs, and deliver better patient experiences.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>$760B-$935B annual healthcare waste</li>
                <li>20% reduction in readmission rates</li>
                <li>Streamlined workflows across departments</li>
                <li>Enhanced compliance and quality metrics</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            {/* TOC */}
            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        "text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 " +
                        (activeId === s.id ? "font-semibold text-slate-900" : "text-slate-700")
                      }
                    >
                      {s.id === "exec-summary" && "Executive Summary"}
                      {s.id === "cost-fragmentation" && "The Cost of Fragmentation"}
                      {s.id === "integration-matters" && "Why Integration Matters Now"}
                      {s.id === "key-areas" && "Key Areas for Coordination"}
                      {s.id === "benefits" && "Benefits of Integrated Platforms"}
                      {s.id === "roadmap" && "Roadmap to Breaking Silos"}
                      {s.id === "conclusion" && "Conclusion"}
                      {s.id === "references" && "References"}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Citation */}
            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX Research Group. "Breaking Silos: How Integrated Care Coordination Improves Outcomes and Reduces Costs." August 25, 2025.
              </p>
              <button
                onClick={() => {
                  const text = 'EceptionistCX Research Group. "Breaking Silos: How Integrated Care Coordination Improves Outcomes and Reduces Costs." August 25, 2025.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Related white papers */}
      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "The Future of Referral Management in Healthcare", description: "From fax to AI-powered workflows that eliminate administrative waste and improve access.", route: "/whitepapers/referral-cx" },
              { title: "Digital Front Doors in Healthcare", description: "Why scheduling matters more than ever for patient experience and provider efficiency.", route: "/whitepapers/scheduling-cx" },
              { title: "Scaling Cell & Gene Therapy Programs", description: "How technology enables safe, efficient, and compliant workflows.", route: "/whitepapers/cell-cx" }
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="Breaking Silos: How Integrated Care Coordination Improves Outcomes and Reduces Costs"
        pdfPath="/PDF/WhitePapers/IntegratedCare_WhitePaper.pdf"
      />
    </div>
  )
}

export default IntegratedCare
